// Jurisdiction resolution helpers (Phase 17). All functions are
// **read-only** against config + employee shape and are safe to call when
// the new config blocks are missing — they degrade gracefully to the legacy
// behavior (federal-only minimum wage; no local income tax).

import { SEED_ZIP_TO_JURISDICTION } from '../data/jurisdictionsSeed';

// Normalize a jurisdiction key ("City, ST") from city + state strings.
export function jurisdictionKeyOf(city, state) {
  if (!city || !state) return null;
  const c = String(city).trim();
  const s = String(state).trim().toUpperCase();
  if (!c || !s) return null;
  return `${c}, ${s}`;
}

// Look up a ZIP in the curated map. Returns { city, state } or null.
export function lookupZip(zip) {
  if (!zip) return null;
  const z = String(zip).trim().slice(0, 5).padStart(5, '0');
  return SEED_ZIP_TO_JURISDICTION[z] || null;
}

// Resolve the most specific employee location available.
// Priority: explicit city  >  ZIP lookup  >  employer HQ  >  state-only.
export function resolveEmployeeLocation(employee, employer) {
  // 1) explicit city + state on the employee record
  if (employee?.city && employee?.state) {
    return {
      city: String(employee.city).trim(),
      state: String(employee.state).trim().toUpperCase(),
      source: 'explicit',
    };
  }
  // 2) ZIP lookup (curated set covers all configured jurisdictions)
  if (employee?.zip) {
    const zhit = lookupZip(employee.zip);
    if (zhit) return { ...zhit, source: 'zip' };
  }
  // 3) employer HQ as a workforce-wide fallback
  if (employer?.city && employer?.state) {
    return {
      city: String(employer.city).trim(),
      state: String(employer.state).trim().toUpperCase(),
      source: 'employer_hq',
    };
  }
  // 4) bare state from employee (no local jurisdiction match possible)
  if (employee?.state) {
    return { city: null, state: String(employee.state).trim().toUpperCase(), source: 'state_only' };
  }
  return { city: null, state: null, source: 'unknown' };
}

// Determine the highest applicable minimum wage for an employee.
// Returns: { applicable, federal, state, city, basis: 'federal'|'state'|'city' }
export function getApplicableMinimumWage(employee, config, employer = null) {
  const j = config?.minimumWageJurisdictions;
  // Legacy fallback: no jurisdiction block configured.
  if (!j) {
    const flat = Number(config?.payrollConstants?.minWageHourly) || 7.25;
    return { applicable: flat, federal: flat, state: null, city: null, basis: 'federal' };
  }
  const federal = Number(j.federal?.hourly) || 7.25;

  const loc = resolveEmployeeLocation(employee || {}, employer || {});
  const stateCode = loc.state;
  const stateMin = stateCode && j.states?.[stateCode]?.hourly
    ? Number(j.states[stateCode].hourly)
    : null;

  const cityKey = jurisdictionKeyOf(loc.city, loc.state);
  const cityMin = cityKey && j.cities?.[cityKey]?.hourly
    ? Number(j.cities[cityKey].hourly)
    : null;

  // Pick max
  let applicable = federal;
  let basis = 'federal';
  if (stateMin && stateMin > applicable) { applicable = stateMin; basis = 'state'; }
  if (cityMin && cityMin > applicable) { applicable = cityMin; basis = 'city'; }

  return { applicable, federal, state: stateMin, city: cityMin, basis, location: loc, jurisdictionKey: cityKey };
}

// Calculate progressive bracket tax using {from, to, rate} bracket shape.
// (Local-tax data uses {from,to,rate}; existing federal/state engine uses
//  {min,max,rate}. Keeping a small dedicated helper keeps shapes isolated.)
function calcLocalProgressive(income, brackets) {
  if (!brackets || !brackets.length || income <= 0) return 0;
  let tax = 0;
  for (const b of brackets) {
    if (income <= b.from) break;
    const top = b.to == null ? Infinity : b.to;
    tax += (Math.min(income, top) - b.from) * b.rate;
  }
  return tax;
}

// Resolve the local income tax entry that applies to a given employee.
export function getLocalTaxEntry(employee, config, employer = null) {
  const map = config?.localIncomeTaxes;
  if (!map || !Object.keys(map).length) return null;
  const loc = resolveEmployeeLocation(employee || {}, employer || {});
  const key = jurisdictionKeyOf(loc.city, loc.state);
  if (!key) return null;
  const entry = map[key];
  if (!entry) return null;
  return { key, entry, location: loc };
}

// Compute local income tax for a given annual income / filing status.
// `entry` is the localIncomeTaxes[key] object. Returns annual tax dollars.
export function computeLocalIncomeTax(entry, annualIncome, filingStatus) {
  if (!entry || !entry.conformsToFederal) return 0;
  if (entry.brackets) {
    // Map filing -> brackets (fallback to single)
    const fk = filingStatus === 'married' || filingStatus === 'qss' ? 'married'
      : filingStatus === 'hoh' || filingStatus === 'head' ? 'hoh'
      : 'single';
    const brackets = entry.brackets[fk] || entry.brackets.single || [];
    return calcLocalProgressive(Math.max(0, annualIncome), brackets);
  }
  if (entry.flatRate != null) {
    // Flat applies to taxable income directly
    return Math.max(0, annualIncome) * Number(entry.flatRate);
  }
  return 0;
}
